// src/components/Sidebar.jsx
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    FiMenu,
    FiChevronLeft,
    FiChevronRight,
    FiHome,
    FiUser,
    FiUsers,
    FiUserPlus,
    FiCheckCircle,
    FiCalendar,
    FiKey,
    FiPlusSquare,
    FiFileText,
    FiSettings,
    FiLayout,
    FiLogOut,
    FiPower
} from 'react-icons/fi';
import logo from '../assets/logo.png';

const NAVY_300 = '#003671';
const NAVY_700 = '#083C76';
const NAVY = '#0f172a';
const NAVY_HOVER = 'rgba(255,255,255,0.08)';

const BORDEAUX = '#8E210E';
const BORDEAUX_700 = '#8E210E';

function Sidebar({ user, collapsed, setCollapsed }) {
    const [menuItems, setMenuItems] = useState([]);
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const iconMap = useMemo(() => ({
        'Panel administratora': FiLayout,
        'Użytkownicy': FiUsers,
        'Egzaminy': FiFileText,

        'Home': FiHome,
        'Moje konto': FiUser,
        'Moje grupy': FiUsers,
        'Dołącz do grupy': FiUserPlus,
        'Egzaminy ukończone': FiCheckCircle,
        'terminy ukończone': FiCheckCircle,
        'Egzaminy Zaplanowane': FiCalendar,
        'Terminy Zaplanowane': FiCalendar,
        'Dołącz do egzaminu za pomocą kodu': FiKey,

        'Strona główna': FiHome,
        'Stwórz egzamin': FiPlusSquare,
        'Grupy': FiUsers,
    }), []);

    useEffect(() => {
        if (!user) return;
        switch (user.rola) {
            case 'admin':
                setMenuItems([
                    { label: 'Panel administratora', path: '/admin' },
                    { label: 'Użytkownicy', path: '/admin/uzytkownicy' },
                    { label: 'Egzaminy', path: '/admin/egzaminy' },
                ]);
                break;
            case 'student':
                setMenuItems([
                    { label: 'Home', path: '/student/' },
                    { label: 'Moje grupy', path: '/student/grupy' },
                    { label: 'Dołącz do grupy', path: '/student/grupa/dodaj-kod' },
                    { label: 'Egzaminy ukończone', path: '/student/egzaminy/zakonczone' },
                    { label: 'Egzaminy Zaplanowane', path: '/student/egzaminy/zaplanowane' },
                    { label: 'Dołącz do egzaminu za pomocą kodu', path: '/student/egzamin/dodaj-kod' },
                ]);
                break;
            case 'prowadzacy':
                setMenuItems([
                    { label: 'Strona główna', path: '/prowadzacy/' },
                    { label: 'Terminy Zaplanowane', path: '/prowadzacy/terminy/zaplanowane' },
                    { label: 'Terminy ukończone', path: '/prowadzacy/terminy/koniec' },
                    { label: 'Egzaminy', path: '/prowadzacy/egzaminy' },
                    { label: 'Grupy', path: '/prowadzacy/grupy' }
                ]);
                break;
            default:
                setMenuItems([]);
        }
    }, [user]);

    const isActive = (itemPath) => {
        if (itemPath === '/student/') return pathname === '/student' || pathname === '/student/';
        if (itemPath === '/prowadzacy/') return pathname === '/prowadzacy' || pathname === '/prowadzacy/';
        return pathname.startsWith(itemPath);
    };



    return (
        <aside
            role="navigation"
            aria-label="Główne menu"
            style={{
                position: 'fixed',
                inset: '0 auto 0 0',
                height: '100vh',
                width: collapsed ? 64 : 250,
                background: 'linear-gradient(to bottom, #0f172a 0 0%, #003671 100% 100%)',
                color: '#fff',
                borderRight: `1px solid ${NAVY_700}`,
                boxShadow: '0 1px 2px rgba(0,0,0,.25)',
                transition: 'width 200ms ease',
                display: 'grid',
                gridTemplateRows: '56px 1fr 56px',
                zIndex: 30
            }}
        >
            {/* Top bar */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: collapsed ? 'center' : 'space-between',
                    padding: collapsed ? '0 6px' : '0 10px'
                }}
            >
                {!collapsed && (
                    <div
                        onClick={() => navigate('/')}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && navigate('/')}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
                        title="Przejdź do strony głównej"
                    >
                        <div
                            style={{
                                width: 50, height: 50, borderRadius: 10,
                                display: 'grid', placeItems: 'center',
                                fontWeight: 800, marginTop:20
                            }}
                        >
                            <img src={logo} alt="Logo" style={{ height: '40px', width: '32px' }} />
                        </div>
                        <div style={{ fontWeight: 300, fontSize: 16, letterSpacing: 1, marginTop:20 }}>
                            Egzamin GAMNS
                        </div>
                    </div>
                )}

                {collapsed ? (
                    <button
                        aria-label="Otwórz menu"
                        onClick={() => setCollapsed(false)}
                        style={circleBtn}
                        title="Menu"
                    >
                        <FiMenu size={18} />
                    </button>
                ) : (
                    <button
                        aria-label="Zwiń menu"
                        onClick={() => setCollapsed(true)}
                        style={circleBtn}
                        title="Zwiń"
                    >
                        <FiChevronLeft size={18} />
                    </button>
                )}
            </div>

                {/* Items */}
                <ul
                    style={{
                            listStyle: 'none',
                            margin: 0,
                            padding: collapsed ? '10px' : '6px',
                            /* kluczowe zmiany: */
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'stretch',
                            justifyContent: 'flex-start',
                            gap: 14,
                            overflowY: 'auto',
                        marginTop:20
                    }}
                >
                        {menuItems.map((item) => {
                                const Icon = iconMap[item.label] || FiSettings;
                                const active = isActive(item.path);
                                return (
                                    <li key={item.path} style={{ /* nic nie rozciągamy */ }}>
                                            <button
                                                onClick={() => navigate(item.path)}
                                                title={collapsed ? item.label : undefined}
                                                style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: collapsed ? 0 : 10,
                                                        width: '100%',
                                                        border: 'none',
                                                        background: active ? 'linear-gradient(to right, #8E210E 0 0%, transparent 100% 100%)' : 'transparent',
                                                        color: '#fff',
                                                        padding: collapsed ? '25px 10px' : '25px 10px',
                                                        height: 40,
                                                        borderRadius: 10,
                                                        cursor: 'pointer',
                                                        transition: 'background 140ms ease, color 140ms ease',
                                                        position: 'relative'
                                                }}
                                                onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = NAVY_HOVER; }}
                                                onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                                                onFocus={(e) => { if (!active) e.currentTarget.style.background = NAVY_HOVER; }}
                                                onBlur={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                                            >
                                                {active && !collapsed && (
                                                    <span
                                                        aria-hidden
                                                        style={{
                                                            position: 'absolute',
                                                            left: 6,
                                                            top: '50%',
                                                            transform: 'translateY(-50%)',
                                                            width: 3,
                                                            height: 18,
                                                            borderRadius: 2,
                                                            background: '#fff',
                                                            opacity: .9
                                                        }}
                                                    />
                                                )}
                                                    <span aria-hidden style={{ display:'inline-flex', width:32, justifyContent:'center', color:'#fff' }}>
            <Icon size={18} />
          </span>
                                                    {!collapsed && (
                                                        <span style={{ fontWeight: active ? 700 : 500, fontSize: 14 }}>
              {item.label}
            </span>
                                                    )}
                                            </button>
                                    </li>
                                );
                        })}
                </ul>


                {/* Bottom actions */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: collapsed ? 'center' : 'space-between',
                    padding: collapsed ? '0 6px' : '0 10px',
                    borderTop: `1px solid ${NAVY_700}`
                }}
            >
                {!collapsed && (
                    <button
                        onClick={() => { localStorage.removeItem('token'); window.location.reload(); }}
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: 8,
                            height: 36, padding: '0 10px',
                            borderRadius: 10, border: '1px solid rgba(255,255,255,.25)',
                            background: 'transparent', color: '#fff', cursor: 'pointer', marginBottom:20
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                        title="Wyloguj"
                    >
                        <FiPower size={25} />
                        <span style={{ fontSize: 15, fontWeight: 400 }}>Wyloguj</span>
                    </button>
                )}
                {!collapsed ? (
                    <button
                        aria-label="Zwiń menu"
                        onClick={() => setCollapsed(true)}
                        style={circleBtnBottom}
                        title="Zwiń"
                    >
                        <FiChevronRight size={18} />
                    </button>
                ) : (
                    <button
                        aria-label="Otwórz menu"
                        onClick={() => setCollapsed(false)}
                        style={circleBtnBottom}
                        title="Menu"
                    >
                        <FiChevronRight size={18} />
                    </button>
                )}
            </div>
        </aside>
    );
}

const circleBtn = {
    width: 34,
    height: 34,
    borderRadius: 10,
    border: '1px solid rgba(255,255,255,.25)',
    background: 'transparent',
    color: '#fff',
    display: 'grid',
    placeItems: 'center',
    cursor: 'pointer',
    marginTop:20
};
const circleBtnBottom = {
    width: 34,
    height: 34,
    borderRadius: 10,
    border: '1px solid rgba(255,255,255,.25)',
    background: 'transparent',
    color: '#fff',
    display: 'grid',
    placeItems: 'center',
    cursor: 'pointer',
    marginBottom:20
};


export default Sidebar;
